import Button from "../../../../shared/ui/button/Button";
import { CheckLineIcon } from "../../../../icons";

interface Feature {
    text: string;
    included: boolean;
}

interface PricingCardProps {
    title: string;
    price?: string;
    period?: string;
    contactLabel?: string;
    description: string;
    features: Feature[];
    buttonText: string;
    isPopular?: boolean;
    onSelect: () => void;
    variant?: "outline" | "primary";
}

export default function PricingCard({
    title,
    price,
    period,
    contactLabel,
    description,
    features,
    buttonText,
    isPopular = false,
    onSelect,
    variant = "outline",
    isDisabled = false,
    footerText,
    isDeep = false,
    badge,
    taxNote,
}: PricingCardProps & {
    isDisabled?: boolean;
    footerText?: string;
    /**
     * The darker end of the brand palette, for the larger size of a plan: set
     * apart from the highlighted one without a colour of its own.
     */
    isDeep?: boolean;
    /** The pill in the corner of a filled card. */
    badge?: string;
    /** Beside the price: what it leaves out — "+ IVA". */
    taxNote?: string;
}) {
    // A filled card — the highlighted one or the deep one — reads in white.
    const filled = isPopular || isDeep;
    return (
        <div
            className={`relative flex flex-col p-6 sm:p-10 rounded-2xl sm:rounded-3xl transition-all duration-500 group ${isPopular
                ? "bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-2xl shadow-brand-200 dark:shadow-brand-950/40 scale-100 sm:hover:scale-[1.02] lg:hover:scale-105 z-10 border-0 overflow-hidden"
                : isDeep
                  ? "bg-gradient-to-br from-brand-800 to-brand-950 text-white shadow-2xl shadow-brand-900/30 dark:shadow-brand-950/40 sm:hover:scale-[1.02] border-0 overflow-hidden"
                  : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-xl sm:hover:-translate-y-1"
                }`}
        >
            {filled && (
                <>
                    {/* Decorative hover blob inspired by Home pending card */}
                    <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-white/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <span className="absolute top-4 right-4 px-3 py-1 text-xs font-bold uppercase tracking-widest text-brand-600 bg-white rounded-full shadow-sm">
                        {badge ?? title}
                    </span>
                </>
            )}

            <div className="flex flex-col h-full relative z-10">
                <div className="mb-6">
                    <h3 className={`text-xl sm:text-2xl font-bold tracking-tight ${filled ? "text-white" : "text-content-primary"}`}>
                        {title}
                    </h3>
                    <p className={`mt-2 text-sm leading-relaxed ${filled ? "text-white/80" : "text-content-tertiary"}`}>
                        {description}
                    </p>
                </div>

                <div className="mb-8">
                    {contactLabel ? (
                        <span className={`text-xl sm:text-2xl font-bold tracking-tight ${filled ? "text-white" : "text-content-primary"}`}>
                            {contactLabel}
                        </span>
                    ) : (
                        <div className="flex items-baseline gap-1">
                            <span className={`text-4xl sm:text-5xl font-extrabold tracking-tighter ${filled ? "text-white" : "text-content-primary"}`}>
                                {price}
                            </span>
                            {period && (
                                <span className={`text-sm sm:text-base font-medium ${filled ? "text-white/70" : "text-content-tertiary"}`}>
                                    /{period}
                                </span>
                            )}
                            {taxNote && (
                                <span className={`ml-1 text-xs sm:text-sm font-medium ${filled ? "text-white/70" : "text-content-tertiary"}`}>
                                    {taxNote}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <ul className="flex-1 mb-8 space-y-4">
                    {features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-3">
                            <div className={`shrink-0 p-1 rounded-full ${filled ? "bg-white/20 text-white" : "bg-brand-50 text-brand-500 dark:bg-brand-500/10"}`}>
                                <CheckLineIcon className="size-3.5" />
                            </div>
                            <span className={`text-sm font-medium ${filled ? "text-white/90" : "text-gray-600 dark:text-gray-300"}`}>
                                {feature.text}
                            </span>
                        </li>
                    ))}
                </ul>

                <div className="mt-auto space-y-4">
                    <Button
                        onClick={onSelect}
                        variant={variant}
                        disabled={isDisabled}
                        className={`w-full justify-center h-12 rounded-2xl font-bold transition-all duration-300 ${filled
                            ? "!bg-white !text-brand-600 hover:!bg-gray-50 hover:scale-[1.02] shadow-lg border-transparent"
                            : "hover:scale-[1.02]"
                            }`}
                    >
                        {buttonText}
                    </Button>
                    {footerText && (
                        <p className={`text-xs text-center font-medium tracking-tight ${filled ? "text-white/60" : "text-content-tertiary"}`}>
                            {footerText}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
